import type { FeedMessageWithMeta } from './types';

/** Enough to push the load-more sentinel off a tall screen, so it can fire again. */
const MIN_VISIBLE = 25;
/** A fetch with actions returns at most 25 messages, so this is ~200 updates. */
const MAX_FETCHES = 8;

export interface FeedPage {
  more?: { start: string | number };
  messages: FeedMessageWithMeta[];
}

interface AddedAction {
  type: string;
  value: string;
  uuid?: string;
  actionTimetoken?: string | number;
}

/**
 * Withdrawn when the update's own publisher — the server — marked it deleted. An action from anyone
 * else is ignored: any client can add one, and the feed is the server's to shape.
 */
export const isWithdrawn = ({ uuid, actions }: FeedMessageWithMeta) =>
  !!uuid && !!actions?.deleted?.deleted?.some((action) => action.uuid === uuid);

/**
 * Fetches until a page holds `MIN_VISIBLE` updates that are not withdrawn, oldest first. A page
 * left empty by withdrawals never moves the sentinel, so the observer would never fire for the next.
 */
export const fetchVisiblePage = async (
  fetchPage: (start: string) => Promise<FeedPage>,
  start: string,
  fetches = 1,
  newer: FeedMessageWithMeta[] = [],
): Promise<FeedPage> => {
  const page = await fetchPage(start);
  const messages = [...page.messages.filter((message) => !isWithdrawn(message)), ...newer];
  if (!page.more || messages.length >= MIN_VISIBLE || fetches >= MAX_FETCHES) {
    return { more: page.more, messages };
  }
  return fetchVisiblePage(fetchPage, String(page.more.start), fetches + 1, messages);
};

/** The update with a live action recorded the way history reports it. */
export const withAction = (message: FeedMessageWithMeta, { type, value, uuid, actionTimetoken }: AddedAction) => ({
  ...message,
  actions: {
    ...message.actions,
    [type]: {
      ...message.actions?.[type],
      [value]: [...(message.actions?.[type]?.[value] ?? []), { uuid: uuid ?? '', actionTimetoken: actionTimetoken ?? '' }],
    },
  },
});
